import { Subject, Subscription } from 'rxjs';

import { Facade } from '@bolteu/lattice';

import { ProviderPortalModuleSetup } from '../Module';
import { ObservableAndState } from '../utils/observable_and_state';

export type NavigationCall = {
  type: 'navigateTo' | 'goBack';
  url?: string;
};

export type LocationChange = {
  pathname: string;
  search: string;
  hash: string;
};

// This is a wrapper around the Lattice Navigation Service
// used to adapt the navigation changes to the app level redux navigation module
export class ProviderPortalNavigationService {
  private currentLocation!: LocationChange;
  constructor(
    private readonly latticeNavigationService: Facade.NavigationService,
    private readonly locationObservable: ObservableAndState<LocationChange>,
    private readonly navigationSubject?,
  ) {
    if (!this.navigationSubject) {
      this.navigationSubject = new Subject<NavigationCall>();
    }

    this.currentLocation = locationObservable.state;
    locationObservable.observable.subscribe((location: LocationChange) => {
      this.currentLocation = location;
    });
  }

  public createForModule(module: ProviderPortalModuleSetup): ProviderPortalNavigationService {
    return new ProviderPortalNavigationService(
      module.latticeNavigationService,
      this.locationObservable,
      this.navigationSubject,
    );
  }

  public navigateTo(target: Facade.NavigationTarget) {
    // We won't call the source function here, because we want to control the navigation
    // this.latticeNavigationService.navigateTo(target, params);

    this.navigationSubject.next({
      type: 'navigateTo',
      url: this.latticeNavigationService.getLink(target),
    });
  }

  public goBack() {
    // We won't call the source function here, because we want to control the navigation
    // this.latticeNavigationService.goBack();

    this.navigationSubject.next({
      type: 'goBack',
    });
  }

  // Get pathname with the module name
  public getFullUrl(): string {
    return this.currentLocation.pathname;
  }

  // Get pathname without the module name
  public getUrl(): string {
    return this.currentLocation.pathname.split('/').slice(2).join('/');
  }

  // Get all URL parts
  public getLocation(): Facade.Location {
    return {
      ...this.currentLocation,
    };
  }

  // Simulate and get the link for a navigation target
  public getLink(target: Facade.NavigationTarget): string {
    return this.latticeNavigationService.getLink(target);
  }

  // Get a subscription to the navigation calls
  public onNavigationCall(callback: (navCall: NavigationCall) => void): Subscription {
    return this.navigationSubject.subscribe(callback);
  }
}
